import { normalizeDateToIso } from './dateNormalization';
import { buildQuoteFundingFields } from './quoteFundingPayload';

/**
 * @param {*} value
 * @returns {string}
 */
function str(value) {
  if (value == null) return '';
  return String(value).trim();
}

/**
 * @param {*} value
 * @returns {string}
 */
function priceStr(value) {
  if (value == null || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return str(value);
  return String(n);
}

/**
 * Map reviewed extraction draft to POST /quotes/ payload (products quote).
 *
 * @param {import('../types/quoteExtractionTypes').ExtractedQuoteData} data
 * @param {Object} context
 * @param {number|string} context.supplierId - Materiah supplier pk (user-selected)
 * @param {number|null} [context.projectId]
 * @param {number|null} [context.userId]
 * @param {string} [context.budget] - User-entered budget amount (required unless funding credit)
 * @param {string|number|null} [context.fundingCreditId] - Supplier credit pk when used instead of budget
 * @param {{ value: unknown, label: string, matchReason?: string }|null} [context.suggestedSupplier]
 * @param {{ formatId: string, displayName: string }|null} [context.resolvedFormat]
 * @returns {import('../types/quoteExtractionTypes').NormalizedQuotePayload}
 */
export function mapExtractedQuoteToCreatePayload(data, context) {
  const headerCurrency = str(data.quote?.currency);
  const demandRef =
    str(data.quote?.quoteNumber) ||
    str(data.customer?.customerNumber) ||
    str(data.customer?.companyId);

  const items = (data.items || []).map(item => {
    const priceBefore =
      item.unitPriceBeforeDiscount ??
      item.unitPrice ??
      item.unitPriceAfterDiscount;
    const priceAfter = item.unitPriceAfterDiscount ?? item.unitPrice;
    const currency =
      str(item.unitPriceCurrency) ||
      str(item.lineTotalCurrency) ||
      headerCurrency;
    const catalog = str(item.catalogNumber) || str(item.productNumber);

    return {
      supplier_catalog_number: catalog,
      product_name: str(item.description),
      unit: str(item.unit),
      quantity: item.quantity != null ? Number(item.quantity) : null,
      price: priceStr(priceBefore),
      currency,
      discount:
        item.discountPercent != null ? String(item.discountPercent) : '',
      discount_type: item.discountPercent != null ? 'percent' : '',
      price_after_discount: priceStr(priceAfter),
    };
  });

  const fundingCreditId =
    context.fundingCreditId != null &&
    String(context.fundingCreditId).trim() !== ''
      ? context.fundingCreditId
      : null;

  const fundingResult = buildQuoteFundingFields({
    budgetLineId: context.budgetLineId,
    budget: str(context.budget),
    fundingCreditId,
    items,
  });
  if (!fundingResult.ok) {
    throw new Error(fundingResult.error);
  }

  const payload = {
    supplier: Number(context.supplierId),
    corporate_demand_ref: demandRef,
    ...fundingResult.fields,
    project: context.projectId != null ? Number(context.projectId) : null,
    user: context.userId != null ? Number(context.userId) : null,
    items,
    extractionMeta: {
      ...data.meta,
      quoteDateIso: normalizeDateToIso(data.quote?.quoteDate),
      validUntilIso: normalizeDateToIso(data.quote?.validUntil),
      supplierSnapshot: data.supplier,
      supplierContactSnapshot: data.supplierContact ?? null,
      customerSnapshot: data.customer,
      deliveryAddressSnapshot: data.deliveryAddress ?? null,
      totalsSnapshot: data.totals ?? null,
      quoteHeaderSnapshot: data.quote,
      itemsSnapshot: (data.items || []).map(it => ({
        casNumber: it.casNumber ?? null,
        productDataSheetUrl: it.productDataSheetUrl ?? null,
        rawUrlLine: it.rawUrlLine ?? null,
        productNumber: it.productNumber ?? null,
      })),
      matchedSupplierLabel: context.suggestedSupplier?.label ?? null,
      matchReason: context.suggestedSupplier?.matchReason ?? null,
      resolvedFormatDisplayName: context.resolvedFormat?.displayName ?? null,
    },
  };

  return payload;
}

/**
 * Deep clone extraction data for editable review state.
 *
 * @param {import('../types/quoteExtractionTypes').ExtractedQuoteData} data
 * @returns {import('../types/quoteExtractionTypes').ExtractedQuoteData}
 */
export function cloneExtractedQuoteData(data) {
  return JSON.parse(JSON.stringify(data));
}

/**
 * Empty line item for manual add row.
 *
 * @param {number} lineNumber
 * @returns {import('../types/quoteExtractionTypes').ExtractedQuoteItem}
 */
export function emptyExtractedQuoteItem(lineNumber = 1) {
  return {
    lineNumber,
    catalogNumber: null,
    productNumber: null,
    description: null,
    casNumber: null,
    eta: null,
    quantity: null,
    unit: null,
    unitPriceBeforeDiscount: null,
    discountPercent: null,
    unitPriceAfterDiscount: null,
    unitPrice: null,
    unitPriceCurrency: null,
    lineTotal: null,
    lineTotalCurrency: null,
    productDataSheetUrl: null,
    rawLine: null,
    rawUrlLine: null,
  };
}
